import { cx } from "@linaria/core";
import { useRef } from "react";

import { getPortals, PM2Portal, CtaComponentCard } from "@courses-pm2/api";
import { parseSimpleHtml } from "@courses-pm2/api/src/ts/parser/parseSimpleHtml";

import { CardButton } from "./atom.button";
import * as styles from "./_card.style";


type Props = {
  portalId: string;
  card: CtaComponentCard;
};

export function Card(props: Props): React.JSX.Element {
  const ref = useRef<HTMLAnchorElement>(null);

  const imageSrc = (() => {
    if (!props.card.imageSrc) {
      return undefined;
    }

    const imageUrl = new URL(props.card.imageSrc);
    imageUrl.searchParams.set("w", "350");

    return imageUrl.toString();
  })();

  const linkTarget = (() => {
    switch (props.card.link.target) {
      case "download":
        return undefined;

      default:
        return "new-tab";
    }
  })();

  const onClick = (evt: React.MouseEvent) => {
    if (props.card.link.target === "download" || props.card.link.target === "new-tab") {
      // let external links be handled natively by the browser
      return;
    }

    evt.preventDefault();

    openInternalLink({
      path: props.card.link.href,
      portalId: props.portalId,
    });
  };

  return (
    <a
      ref={ref}
      className={cx(
        styles.wrapper,
        !props.card.imageSrc && styles.modifiers.noImage
      )}
      data-color-variant={props.card.meta.colorVariant}
      href={props.card.link.href}
      download={props.card.link.target === "download" ? true : undefined}
      target={linkTarget}
      onClick={onClick}
    >
      {imageSrc && (
        <img
          className={styles.image}
          src={imageSrc}
          alt=""
          role="presentation"
        />
      )}

      <div className={styles.textWrapper}>
        <h3 className={styles.heading}>{props.card.title}</h3>

        {!!props.card.description && (<p className={styles.description}>
          {parseSimpleHtml(props.card.description)}
        </p>)}
      </div>

      <CardButton
        icon={props.card.link.target}
        theme={props.card.imageSrc ? "black" : "accent"}
      />
    </a>
  );
}

/**
 * Internal cache of portals available within the PM2 backend, so that we can
 * safely resolve links to them.
 */
let cache: PM2Portal[] | undefined;

/**
 * Helper that'll open an internal link on a pm2 portal in a new window (first
 * resolving the base url of the portal).
 */
export async function openInternalLink({ path, portalId }: {
    path: string;
    portalId: string;
}): Promise<void> {
    // Open a new window instantly, so it will occur directly within the user's
    // input event
    const win = window.open("about:blank", "_blank");

    // Bail out if we were unable to open the new window
    if (!win) {
        alert("Vi kunne ikke åbne linket, vi beklager.");

        // Abort further execution
        return;
    }

    try {
        // If portals haven't been fetched, then do so now
        if (!cache) {
            cache = await getPortals();
        }

        const portal = cache.find((tmpPortal) => tmpPortal.id === portalId);

        // Bail out if unable to find the portal
        if (!portal || !portal.primaryDomainName) {
            throw new Error("openInternalLink(...): Unable to find portal primary domain.");
        }

        // Determine if we're supposed to use http or https when accessing the
        // portal based on the primary domain
        const domain = (
            portal.primaryDomainName.indexOf("//") !== -1
                ? portal.primaryDomainName
                    .split("//")[1]
                    .split("/")[0]
                : portal.primaryDomainName
                    .split("/")[0]
        );

        const protocol = (
            domain.match(/^[^\.]+\.(gyldendal|munksgaard|hansreitzel)\.dk$/i)
                ? "https://"
                : "http://" // tslint:disable-line no-http-string
        );

        // Generate complete url
        const url = `${protocol}${domain}/${path.replace(/^\//, "")}`;

        // Open the url!
        win.location.href = url;
    } catch (err) {
        // Propagate the error to the console
        console.error(err); // tslint:disable-line no-console

        // Notify the user of the issue with opening the page
        win.alert("Vi kunne ikke åbne linket, vi beklager.");
        win.close();
    }
}
